import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { Button } from "~/components/ui/button";
import type { GoalBannerLevel } from "~/hooks/learning-center/transformers";
import useLearningCenterMutation from "~/hooks/learning-center/use-learning-center-mutation";
import useLearningCenter from "~/hooks/use-learning-center";

import messages from "./messages";

/**
 * Port of hec-composable `goal-banner/goalBannerBtn.jsx`. The per-level CTA:
 * "Get Started" / "Continue" / "Review". A not-complete click starts the
 * goal/level/next-module progress (mutations) then navigates into the module;
 * a complete level just navigates to its first module ("Review").
 *
 * Delta vs composable: `useNavigate` (RR7) replaces `useHistory().push`; the
 * progress data is threaded the same way (startGoal → startLevel → startModule),
 * adapted to the Workers mutation signatures.
 */
const GoalBannerBtn = ({ levelData }: { levelData: GoalBannerLevel }) => {
  const {
    nextModuleID,
    nextModuleUrl,
    firstModuleUrl,
    complete,
    started,
    levelID,
    parentGoalID,
  } = levelData;
  const [requestInProgress, setRequestInProgress] = useState(false);
  const navigate = useNavigate();
  const intl = useIntl();
  const { getActiveGoal, learningCenterData } = useLearningCenter();
  const { startGoal, startLevel, startModule, setActiveLesson } =
    useLearningCenterMutation();

  const ctaText = complete
    ? intl.formatMessage(messages.ctaReview)
    : started
      ? intl.formatMessage(messages.ctaContinue)
      : intl.formatMessage(messages.ctaGetStarted);

  const handleClick = async () => {
    // Already done → straight to the level's first module for review.
    if (complete) {
      if (firstModuleUrl) navigate(firstModuleUrl);
      return;
    }
    // No resolvable next module → best-effort navigate, no progress write.
    if (!nextModuleID || !levelID) {
      const url = nextModuleUrl || firstModuleUrl;
      if (url) navigate(url);
      return;
    }

    setRequestInProgress(true);
    try {
      const activeGoal = getActiveGoal();
      let data = structuredClone(learningCenterData);
      // Open the goal first if it isn't the active one (startGoal persists and
      // returns the new data as a JSON string).
      if (activeGoal?.id !== parentGoalID) {
        const res = await startGoal(parentGoalID);
        if (res.data) data = JSON.parse(res.data) as typeof learningCenterData;
      }
      // Seed the level (mutates `data` in place) before starting its module.
      if (!started) startLevel(levelID, parentGoalID, data);

      const response = await startModule(
        nextModuleID,
        levelID,
        data,
        nextModuleUrl,
      );
      if (!response.success) {
        throw new Error(
          ("errorMsg" in response && response.errorMsg) ||
            "Failed to start module",
        );
      }
      await setActiveLesson(firstModuleUrl);
      if (nextModuleUrl) navigate(nextModuleUrl);
    } finally {
      setRequestInProgress(false);
    }
  };

  return (
    <Button
      type="button"
      variant="cta"
      className="ml-0 mt-4 w-full lg:mt-0 lg:w-[12.25rem]"
      onClick={handleClick}
      disabled={requestInProgress}
      data-testid="goal-banner-btn"
    >
      {ctaText}
    </Button>
  );
};

export default GoalBannerBtn;
